import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MapPin, MessageCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/inquiries`, formData);
      
      if (response.data.success) {
        toast.success(
          language === 'en' ? 'Success!' : 'نجح!',
          {
            description: language === 'en' 
              ? 'Your inquiry has been submitted. We will contact you within 24 hours.'
              : 'تم إرسال استفسارك. سنتواصل معك خلال 24 ساعة.',
          }
        );

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(
        language === 'en' ? 'Error' : 'خطأ',
        {
          description: language === 'en' 
            ? error.response?.data?.detail || 'Something went wrong. Please try again.'
            : 'حدت خطأ. يرجى المحاولة مرة أخرى.',
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/905346635111', '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#003d5c] mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">{t.contact.form.name}</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">{t.contact.form.email}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone">{t.contact.form.phone}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="company">{t.contact.form.company}</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="service">{t.contact.form.service}</Label>
                <Select onValueChange={handleServiceChange} value={formData.service}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={t.contact.form.service} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ocean">{t.contact.form.serviceOptions.ocean}</SelectItem>
                    <SelectItem value="sourcing">{t.contact.form.serviceOptions.sourcing}</SelectItem>
                    <SelectItem value="customs">{t.contact.form.serviceOptions.customs}</SelectItem>
                    <SelectItem value="other">{t.contact.form.serviceOptions.other}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">{t.contact.form.message}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#003d5c] hover:bg-[#002a40] text-white py-6 text-lg transition-all duration-300 hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    {language === 'en' ? 'Submitting...' : 'جاري الإرسال...'}
                  </>
                ) : (
                  t.contact.form.submit
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-[#003d5c] to-[#005580] text-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-6">{t.contact.info.title}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">{t.contact.info.office}</p>
                    <p className="text-blue-100 mt-1">{t.contact.info.officeAddress}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MessageCircle className="h-6 w-6 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">{t.contact.info.whatsapp}</p>
                    <a href="https://wa.me/905346635111" className="text-blue-100 hover:text-white transition-colors mt-1 block">
                      +90 534 663 51 11
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">{t.contact.info.email}</p>
                    <a href="mailto:info@cozeyyildiz.com" className="text-blue-100 hover:text-white transition-colors mt-1 block">
                      info@cozeyyildiz.com
                    </a>
                  </div>
                </div>
              </div>

              <Button
                onClick={openWhatsApp}
                className="w-full mt-8 bg-green-500 hover:bg-green-600 text-white py-6 text-lg transition-all duration-300 hover:shadow-xl"
              >
                <MessageCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {language === 'en' ? 'Chat on WhatsApp' : 'تحدث على واتساب'}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-[#003d5c] mb-4">
                {language === 'en' ? 'Why Choose Us?' : 'لماذا تختارنا؟'}
              </h3>
              <div className="space-y-3 text-gray-600">
                <p className="flex items-center">
                  <span className="w-2 h-2 bg-[#003d5c] rounded-full mr-3"></span>
                  {language === 'en' ? '24/7 Customer Support' : 'دعم عملاء 24/7'}
                </p>
                <p className="flex items-center">
                  <span className="w-2 h-2 bg-[#003d5c] rounded-full mr-3"></span>
                  {language === 'en' ? 'Competitive Pricing' : 'أسعار تنافسية'}
                </p>
                <p className="flex items-center">
                  <span className="w-2 h-2 bg-[#003d5c] rounded-full mr-3"></span>
                  {language === 'en' ? 'Fast Customs Clearance' : 'تخليص جمركي سريع'}
                </p>
                <p className="flex items-center">
                  <span className="w-2 h-2 bg-[#003d5c] rounded-full mr-3"></span>
                  {language === 'en' ? 'Global Network' : 'شبكة عالمية'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;